import { GqlClientService } from '@/common/network/gql-client-service'
import * as op from '../../common/errors/OperationResult'
import { type OperationResult } from '@/common/errors/OperationResult'
import type { CreateObjectTypeInput } from '../gql/CreateObjectTypeInput'
import type { CreatePropertyDefinitionInput } from '../gql/CreatePropertyDefinitionInput'
import type { UpdatePropertyDefinitionInput } from '../gql/UpdatePropertyDefinitionInput'
import type { BatchUpdateTypeInput, BatchUpdateTypeResponse } from '../gql/BatchUpdateTypeInput'
import type { PropertyDefinitionModel, ObjectTypeModel } from '@/core/gql/gql-models-override.ts'

type CreateTypeResponse = { createObjectType: ObjectTypeModel }
type TypesResponse = { objectTypes: ObjectTypeModel[] }
type CreatePropDefResponse = { createPropertyDefinition: PropertyDefinitionModel }
type UpdatePropDefResponse = { updatePropertyDefinition: PropertyDefinitionModel }
type BatchUpdateTypeGqlResponse = { updateObjectType?: ObjectTypeModel } & Record<string, unknown>

const propDefFields = `
  id
  name
  type
  isMeta
  objectTypeId
  options {
    id
    value
    color
  }`

export class ObjectTypesApiService {
  constructor(private readonly gqlClientService: GqlClientService) {}

  public async createType(input: CreateObjectTypeInput): Promise<OperationResult<ObjectTypeModel>> {
    const query = `
       mutation CreateObjectType($input: CreateObjectTypeInput!) {
      createObjectType(input: $input) {
        id
        name
        spaceId
        propertyDefinitions {${propDefFields}}
      }
    }`

    const { result: body, error } = await this.gqlClientService.sendGqlRequest<CreateTypeResponse>(
      query,
      {
        variables: {
          input,
        },
      },
    )

    if (error) {
      return op.error(error)
    }

    return op.success(body!.data!.createObjectType)
  }

  public async fetchTypes(
    spaceId: number,
    abortSignal?: AbortSignal,
  ): Promise<OperationResult<ObjectTypeModel[]>> {
    const query = `
query ($spaceId: ID!) {
  objectTypes(spaceId: $spaceId) {
    id
    name
    spaceId
    propertyDefinitions {${propDefFields}}
  }
}
`
    const { result: body, error } = await this.gqlClientService.sendGqlRequest<TypesResponse>(
      query,
      {
        variables: {
          spaceId,
        },
        abortSignal,
      },
    )

    if (error) {
      return op.error(error)
    }
    return op.success(body!.data!.objectTypes)
  }

  public async createPropertyDef(
    input: CreatePropertyDefinitionInput & { objectTypeId: number },
  ): Promise<OperationResult<PropertyDefinitionModel>> {
    const query = `mutation($input: CreatePropertyDefinitionInput!) {
  createPropertyDefinition(input: $input) {${propDefFields}
  }
}`
    const { error, result } = await this.gqlClientService.sendGqlRequest<CreatePropDefResponse>(
      query,
      { variables: { input } },
    )
    if (error) {
      return op.error(error)
    }
    return op.success(result!.data!.createPropertyDefinition)
  }

  public async updatePropertyDef(
    input: UpdatePropertyDefinitionInput,
  ): Promise<OperationResult<PropertyDefinitionModel>> {
    const query = `mutation($input: UpdatePropertyDefinitionInput!) {
  updatePropertyDefinition(input: $input) {${propDefFields}
  }
}`
    const { error, result } = await this.gqlClientService.sendGqlRequest<UpdatePropDefResponse>(
      query,
      {
        variables: {
          input,
        },
      },
    )
    if (error) {
      return op.error(error)
    }
    return op.success(result!.data!.updatePropertyDefinition)
  }

  public async renameType(id: number, name: string): Promise<OperationResult<ObjectTypeModel>> {
    const query = `mutation($input: UpdateObjectTypeInput!) {
  updateObjectType(input: $input) {
    id
    name
    spaceId
    propertyDefinitions {${propDefFields}}
  }
}`
    const { error, result } = await this.gqlClientService.sendGqlRequest<{
      updateObjectType: ObjectTypeModel
    }>(query, { variables: { input: { id, name } } })

    if (error) {
      return op.error(error)
    }
    return op.success(result!.data!.updateObjectType)
  }

  public async batchUpdateType(
    input: BatchUpdateTypeInput,
  ): Promise<OperationResult<BatchUpdateTypeResponse>> {
    const propUpdates = input.propUpdates ?? []
    if (!input.typeInput && propUpdates.length === 0) return op.error('batchUpdateType: no changes')

    const paramList: string[] = []
    const mutationList: string[] = []
    const variables: Record<string, unknown> = {}

    if (input.typeInput) {
      paramList.push('$typeInput: UpdateObjectTypeInput!')
      mutationList.push(
        `  updateObjectType(input: $typeInput) {\n    id name spaceId\n    propertyDefinitions {${propDefFields}}\n  }`,
      )
      variables.typeInput = input.typeInput
    }

    for (let i = 0; i < propUpdates.length; i++) {
      paramList.push(`$upd${i}: UpdatePropertyDefinitionInput!`)
      mutationList.push(`  upd${i}: updatePropertyDefinition(input: $upd${i}) {${propDefFields}\n  }`)
      variables[`upd${i}`] = propUpdates[i]
    }

    const query = `mutation BatchUpdateType(${paramList.join(', ')}) {\n${mutationList.join('\n')}\n}`
    const { error, result } = await this.gqlClientService.sendGqlRequest<BatchUpdateTypeGqlResponse>(
      query,
      { variables },
    )
    if (error) return op.error(error)

    const data = result!.data!
    const updatedPropDefs: PropertyDefinitionModel[] = []
    for (let i = 0; i < propUpdates.length; i++) {
      updatedPropDefs.push(data[`upd${i}`] as PropertyDefinitionModel)
    }

    if (input.typeInput) {
      const type = data.updateObjectType as ObjectTypeModel
      const propDefs = [...type.propertyDefinitions]
      for (const upd of updatedPropDefs) {
        const idx = propDefs.findIndex((p) => p.id === upd.id)
        if (idx !== -1) propDefs[idx] = upd
      }
      return op.success({ updatedType: { ...type, propertyDefinitions: propDefs }, updatedPropDefs })
    }

    return op.success({ updatedType: null, updatedPropDefs })
  }

  public async deleteType(id: number): Promise<OperationResult> {
    const query = `mutation($id: ID!) {
  deleteObjectType(id: $id)
}`
    const { error } = await this.gqlClientService.sendGqlRequest<{ deleteObjectType: boolean }>(
      query,
      { variables: { id } },
    )

    if (error) {
      return op.error(error)
    }
    return op.success()
  }

  //   public async deleteSpace(id: number): Promise<OperationResult> {
  //     const mutation = `mutation($input: DeleteSpaceInput!) {
  //   deleteSpace(input: $input)
  // }`
  //     const { error } = await this.gqlClientService.sendGqlRequest<GqlResponse<unknown>>(mutation, {
  //       variables: {
  //         input: { id } as DeleteSpaceInput,
  //       },
  //     })
  //
  //     if (error) {
  //       return op.error(error)
  //     }
  //
  //     const predicate = (x: SpaceGqlModel) => x.id == id
  //     const spaces = this.types.value!.filter((x) => !predicate(x))
  //     assertLog(spaces.length == this.types.value!.length + 1, 'spaces need to be -1 after delete')
  //
  //     this.types.next(spaces)
  //     return op.success()
  //   }
}
